import { Component, computed, inject, signal } from '@angular/core';
import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import {
  DEFAULT_TOASTER_LIFE,
  IWizardContext,
  WizardStepComponent,
  ZendraLocalizationServices,
  ZnButton,
} from '@zendra/shared';

import { StandGis } from '../../stand/stand-gis/stand-gis';
import type { IResultingStandRow } from '../interfaces/IResultingStandRow';
import type { IStepDefineResultingStandsData } from '../interfaces/IStepDefineResultingStandsData';
import type { IStepSelectStandToDivideData } from '../interfaces/IStepSelectStandToDivideData';

type AreaInputValue = string | number | null;

interface SelectedStandBaseData {
  id: string;
  name: string;
  area: number | null;
}

@Component({
  selector: 'lib-step-define-resulting-stands',
  standalone: true,
  imports: [LocalizationPipe, StandGis, ZnButton],
  templateUrl: './step-define-resulting-stands.html',
})
export class StepDefineResultingStands
  implements WizardStepComponent<IStepDefineResultingStandsData>
{
  private readonly toaster = inject(ToasterService);

  readonly localizationKeyPrefix = 'Geographic::StandDivision.StepDefineResultingStands';
  readonly localizationResource = ZendraLocalizationServices.LanguageService;
  readonly previousStepId = 1;

  readonly selectedStand = signal<SelectedStandBaseData | null>(null);
  readonly originalStandAreaInput = signal<number | null>(null);
  readonly confirmedOriginalStand = signal<IResultingStandRow | null>(null);
  readonly newStandName = signal('');
  readonly newStandAreaInput = signal<number | null>(null);
  readonly resultingStands = signal<IResultingStandRow[]>([]);

  readonly originalSectionClass = computed(() =>
    this.confirmedOriginalStand()
      ? 'zn-step-content-section stand-division-section stand-division-section--form'
      : 'zn-step-content-section zn-step-content-section--selected stand-division-section stand-division-section--form stand-division-section--original',
  );

  readonly tableSectionClass = computed(() =>
    this.confirmedOriginalStand()
      ? 'zn-full-height zn-step-content-section zn-step-content-section--selected stand-division-section stand-division-section--table'
      : 'zn-full-height zn-step-content-section stand-division-section stand-division-section--table',
  );

  readonly allRows = computed(() => {
    const originalStand = this.confirmedOriginalStand();

    return originalStand
      ? [originalStand, ...this.resultingStands()]
      : this.resultingStands();
  });

  readonly totalArea = computed(() =>
    this.allRows().reduce((sum, row) => sum + row.area, 0),
  );

  readonly sectionTitleKey = computed(() =>
    this.confirmedOriginalStand()
      ? `${this.localizationKeyPrefix}.ResultingForm.Title`
      : `${this.localizationKeyPrefix}.Original.Title`,
  );

  readonly sectionDescriptionKey = computed(() =>
    this.confirmedOriginalStand()
      ? `${this.localizationKeyPrefix}.ResultingForm.Description`
      : `${this.localizationKeyPrefix}.Original.Description`,
  );

  readonly canFetchOriginalGisArea = computed(() => !!this.selectedStand()?.name);
  readonly canFetchNewStandGisArea = computed(() => !!this.newStandName().trim());
  readonly originalStandArea = computed(() => this.selectedStand()?.area ?? null);
  readonly originalStandNewArea = computed(() => this.confirmedOriginalStand()?.area ?? null);

  readonly canConfirmOriginalStand = computed(() => this.originalStandAreaInput() !== null);
  readonly canConfirmNewStand = computed(() =>
    !!this.newStandName().trim() && this.newStandAreaInput() !== null,
  );

  readonly editingRowId = signal<string | null>(null);

  onActivate(context: IWizardContext): void {
    context.patchStepState(context.activeStepId(), {
      visited: true,
    });

    const selectedStandData =
      context.getStepState<IStepSelectStandToDivideData>(this.previousStepId)?.data;
    const selectedStandId = selectedStandData?.standId?.toString() ?? null;
    const defaultStand = this.resolveSelectedStand(selectedStandData);

    const currentState =
      context.getStepState<IStepDefineResultingStandsData>(context.activeStepId())?.data;

    if (currentState && !this.hasSelectedStandChanged(currentState, selectedStandId)) {
      this.restoreState(currentState, defaultStand);
      return;
    }

    this.resetState(defaultStand);

    if (currentState) {
      context.patchStepState(context.activeStepId(), {
        valid: false,
        completed: false,
        data: undefined,
        summary: [],
      });
    }

  }

  canLeave(): boolean {
    if (!this.confirmedOriginalStand()) {
      this.notifyError('OriginalConfirmationRequired');
      return false;
    }

    if (this.resultingStands().length === 0) {
      this.notifyError('AtLeastOneResultingStand');
      return false;
    }

    return true;
  }

  canLeaveSync(): boolean {
    return true;
  }

  collect(context: IWizardContext): IStepDefineResultingStandsData {
    const data: IStepDefineResultingStandsData = {
      sourceStandId: this.selectedStand()?.id ?? null,
      originalStandId: this.selectedStand()?.id ?? null,
      originalStandName: this.selectedStand()?.name ?? null,
      originalStandArea: this.confirmedOriginalStand()?.area ?? null,
      resultingStands: this.resultingStands(),
      rows: this.allRows(),
      totalArea: this.roundArea(this.totalArea()),
    };

    context.patchStepState(context.activeStepId(), {
      visited: true,
      valid: !!this.confirmedOriginalStand() && this.resultingStands().length > 0,
      completed: true,
      data,
    });
    return data;
  }

  onOriginalAreaInput(value: AreaInputValue): void {
    const parsedValue = this.parseArea(value);
    const confirmedArea = this.confirmedOriginalStand()?.area ?? null;

    this.originalStandAreaInput.set(parsedValue);

    if (confirmedArea !== null && parsedValue !== confirmedArea) {
      this.resetConfirmedFlow();
    }
  }

  onOriginalGisAreaUpdated(area: number): void {
    this.onOriginalAreaInput(area);
  }

  confirmOriginalStand(): void {
    const selectedStand = this.selectedStand();
    const area = this.originalStandAreaInput();

    if (!selectedStand || area === null || area <= 0) {
      this.notifyError('OriginalAreaRequired');
      return;
    }

    const currentArea = selectedStand.area;

    if (currentArea !== null && area >= currentArea) {
      this.notifyError('OriginalAreaMustBeSmaller');
      return;
    }

    this.confirmedOriginalStand.set({
      id: 'original-stand',
      name: selectedStand.name,
      area,
      type: 'original',
    });
    
    this.editingRowId.set(null);
  }

  onNewStandNameInput(value: string): void {
    this.newStandName.set(value);
  }

  onNewStandAreaInput(value: AreaInputValue): void {
    this.newStandAreaInput.set(this.parseArea(value));
  }

  onNewStandGisAreaUpdated(area: number): void {
    this.onNewStandAreaInput(area);
  }

  addResultingStand(): void {
    if (!this.confirmedOriginalStand()) {
      this.notifyError('OriginalConfirmationRequired');
      return;
    }

    const name = this.newStandName().trim();
    const area = this.newStandAreaInput();

    if (!name) {
      this.notifyError('ResultingStandRequired');
      return;
    }

    if (area === null || area <= 0) {
      this.notifyError('ResultingAreaRequired');
      return;
    }

    this.resultingStands.update(rows => [
      ...rows,
      {
        id: this.buildRowId(name, rows.length),
        name,
        area,
        type: 'resulting',
      },
    ]);

    this.editingRowId.set(null);
    this.resetNewStandForm();
  }

  removeResultingStand(rowId: string): void {
    this.resultingStands.update(rows => rows.filter(row => row.id !== rowId));
  }

  editStand(row: IResultingStandRow): void {
    this.editingRowId.set(row.id);

    if (row.type === 'original') {
      this.originalStandAreaInput.set(row.area);

      this.confirmedOriginalStand.set(null);

      return;
    }

    this.newStandName.set(row.name);
    this.newStandAreaInput.set(row.area);

    this.resultingStands.update(rows =>
      rows.filter(r => r.id !== row.id),
    );
  }

  formatArea(area: number | null | undefined): string {
    if (area === null || area === undefined) {
      return '';
    }

    return area.toFixed(2).replace('.', ',');
  }

  rowOriginalArea(row: IResultingStandRow): number | null {
    return row.type === 'original' ? this.originalStandArea() : null;
  }

  rowNewArea(row: IResultingStandRow): number | null {
    return row.area;
  }

  rowTypeLabelKey(row: IResultingStandRow): string {
    return `${this.localizationKeyPrefix}.Table.Types.${row.type === 'original' ? 'Original' : 'Resulting'}`;
  }

  private restoreState(
    data: IStepDefineResultingStandsData,
    defaultStand: SelectedStandBaseData | null,
  ): void {
    const originalStandId = data.originalStandId ?? defaultStand?.id ?? null;
    const originalStandName = data.originalStandName ?? defaultStand?.name ?? originalStandId ?? '';
    const originalStandArea =
      data.originalStandArea !== null && data.originalStandArea !== undefined
        ? this.roundArea(data.originalStandArea)
        : null;

    this.selectedStand.set(
      defaultStand ??
        (originalStandId
          ? {
              id: originalStandId,
              name: originalStandName,
              area: originalStandArea,
            }
          : null),
    );

    this.originalStandAreaInput.set(originalStandArea);
    this.confirmedOriginalStand.set(
      originalStandName && originalStandArea !== null
        ? {
            id: 'original-stand',
            name: originalStandName,
            area: originalStandArea,
            type: 'original',
          }
        : null,
    );

    this.resultingStands.set(
      (data.resultingStands ?? []).map((row, index) => ({
        id: row.id || this.buildRowId(row.name, index),
        name: row.name,
        area: this.roundArea(row.area),
        type: 'resulting',
      })),
    );
    this.resetNewStandForm();
  }

  private hasSelectedStandChanged(
    data: IStepDefineResultingStandsData,
    selectedStandId: string | null,
  ): boolean {
    return data.sourceStandId !== undefined && data.sourceStandId !== selectedStandId;
  }

  private resetState(defaultStand: SelectedStandBaseData | null): void {
    this.selectedStand.set(defaultStand);
    this.originalStandAreaInput.set(null);
    this.confirmedOriginalStand.set(null);
    this.resultingStands.set([]);
    this.editingRowId.set(null);
    this.resetNewStandForm();
  }

  private resolveSelectedStand(
      selectedStandData?: IStepSelectStandToDivideData,
  ): SelectedStandBaseData | null {
    if (
        selectedStandData?.standId === null ||
        selectedStandData?.standId === undefined
    ) {
        return null;
    }

    const standId = String(selectedStandData.standId);

    return {
      id: standId,
      name: selectedStandData.standName ?? standId,
      area: selectedStandData.standArea ?? null,
    };
  }

  private resetConfirmedFlow(): void {
    this.confirmedOriginalStand.set(null);
    this.resultingStands.set([]);
    this.resetNewStandForm();
  }

  private resetNewStandForm(): void {
    this.newStandName.set('');
    this.newStandAreaInput.set(null);
  }

  private parseArea(value: AreaInputValue): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue) || numericValue <= 0) {
      return null;
    }

    return this.roundArea(numericValue);
  }

  private roundArea(value: number): number {
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  private buildRowId(name: string, index: number): string {
    return `${name.trim().toLowerCase().replace(/\s+/g, '-')}-${index + 1}`;
  }

  private notifyError(key: string): void {
    this.toaster.error(
      `${ZendraLocalizationServices.Geographic}::StandDivision.StepDefineResultingStands.${key}`,
      '',
      { life: DEFAULT_TOASTER_LIFE },
    );
  }
}
